import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, Protected, PublicOnly } from "@/context/AuthContext";
import PortalLayout from "@/components/PortalLayout";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import { PublicDoctors, PublicDoctorProfile, Legal, Help } from "@/pages/PublicPages";
import ConsultationRoom from "@/pages/ConsultationRoom";
import SharedAI from "@/pages/SharedAI";
import PayReturn from "@/pages/PayReturn";
import HealthTrends from "@/pages/patient/HealthTrends";
import { PatientHome, PatientFindDoctor, PatientAppointments, PatientPayments } from "@/pages/patient/PatientPages";
import { BookDoctor, PatientAppointmentDetail } from "@/pages/patient/PatientBooking";
import { HealthRecords, PatientProfile } from "@/pages/patient/PatientRecords";
import { VerificationGate, DoctorHome, DoctorAppointments, DoctorPrescriptions, DoctorEarnings, DoctorAvailability } from "@/pages/doctor/DoctorPages";
import DoctorProfile from "@/pages/doctor/DoctorProfile";
import ClinicalWorkspace from "@/pages/doctor/ClinicalWorkspace";
import { AdminOverview, AdminDoctors, AdminPatients, AdminAppointments } from "@/pages/admin/AdminPages";
import { DoctorReviewsPage, AdminReviews } from "@/pages/ReviewPages";
import DownloadApp from "@/pages/DownloadApp";
import { ReportConsult, JointConsult } from "@/pages/patient/SpecialConsults";
import PartnerPortal from "@/pages/partner/PartnerPortal";
import { PartnerHome, PartnerProfile, PartnerEarnings } from "@/pages/partner/PartnerPages";
import { AdminUsers } from "@/pages/admin/AdminUsers";
import { SuvidhaCentre, PartnerStore } from "@/pages/patient/SuvidhaCentre";
import { Orders } from "@/pages/patient/Orders";
import Family from "@/pages/patient/Family";
import "@/components/Theme";
import { AdminLogin } from "@/pages/Auth";
import { AdminPartners } from "@/pages/admin/AdminPartners";
import AdminSms from "@/pages/admin/AdminSms";
import QConsole from "@/pages/devops/QConsole";
import { AdminDoctorEdit, AdminPartnerEdit } from "@/pages/admin/AdminEditOnBehalf";
import { QLogin } from "@/pages/Auth";
import { ScrollToTop, Floating } from "@/components/Extras";
import { AdminFinance, AdminSupport, AdminSettings, AdminAudit, AdminSecurity } from "@/pages/admin/AdminOps";

const G = (el) => <VerificationGate>{el}</VerificationGate>;

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
        <Floating />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}

function AppRoutes() {
  return (
        <Routes>
          <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
          <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
          <Route path="/doctors" element={<PublicDoctors />} />
          <Route path="/doctors/:id" element={<PublicDoctorProfile />} />
          <Route path="/privacy" element={<Legal kind="privacy" />} />
          <Route path="/terms" element={<Legal kind="terms" />} />
          <Route path="/terms/doctors" element={<Legal kind="terms-doctors" />} />
          <Route path="/privacy/doctors" element={<Legal kind="privacy-doctors" />} />
          <Route path="/terms/partners" element={<Legal kind="terms-partners" />} />
          <Route path="/privacy/partners" element={<Legal kind="privacy-partners" />} />
          <Route path="/cookies" element={<Legal kind="cookies" />} />
          <Route path="/partners" element={<PublicOnly><PartnerPortal /></PublicOnly>} />
          <Route path="/admin-login" element={<PublicOnly><AdminLogin /></PublicOnly>} />
          <Route path="/q-log" element={<QLogin />} />
          <Route path="/devops" element={<Protected role="devops"><QConsole /></Protected>} />
          <Route path="/partner" element={<Protected role="partner"><PortalLayout role="partner" /></Protected>}>
            <Route index element={<PartnerHome />} />
            <Route path="profile" element={<PartnerProfile />} />
            <Route path="earnings" element={<PartnerEarnings />} />
          </Route>
          <Route path="/help" element={<Help />} />
          <Route path="/download" element={<DownloadApp />} />
          <Route path="/shared/ai/:token" element={<SharedAI />} />
          <Route path="/pay/return" element={<PayReturn />} />
          <Route path="/consultation/:id" element={<Protected role="patient"><ConsultationRoom /></Protected>} />
          <Route path="/patient" element={<Protected role="patient"><PortalLayout role="patient" /></Protected>}>
            <Route index element={<PatientHome />} />
            <Route path="find-doctor" element={<PatientFindDoctor />} />
            <Route path="report-consult" element={<ReportConsult />} />
            <Route path="joint-consult" element={<JointConsult />} />
            <Route path="book/:id" element={<BookDoctor />} />
            <Route path="appointments" element={<PatientAppointments />} />
            <Route path="appointments/:id" element={<PatientAppointmentDetail />} />
            <Route path="records" element={<HealthRecords />} />
            <Route path="trends" element={<HealthTrends />} />
            <Route path="profile" element={<PatientProfile />} />
            <Route path="payments" element={<PatientPayments />} />
            <Route path="suvidha" element={<SuvidhaCentre />} />
            <Route path="suvidha/partner/:pid" element={<PartnerStore />} />
            <Route path="market" element={<Navigate to="/patient/suvidha" replace />} />
            <Route path="family" element={<Family />} />
            <Route path="orders" element={<Orders />} />
          </Route>
          <Route path="/doctor" element={<Protected role="doctor"><PortalLayout role="doctor" /></Protected>}>
            <Route index element={G(<DoctorHome />)} />
            <Route path="appointments" element={G(<DoctorAppointments />)} />
            <Route path="appointments/:id" element={G(<ClinicalWorkspace />)} />
            <Route path="availability" element={<DoctorAvailability />} />
            <Route path="prescriptions" element={G(<DoctorPrescriptions />)} />
            <Route path="earnings" element={G(<DoctorEarnings />)} />
            <Route path="reviews" element={G(<DoctorReviewsPage />)} />
            <Route path="profile" element={<DoctorProfile />} />
          </Route>
          <Route path="/admin" element={<Protected role="admin"><PortalLayout role="admin" /></Protected>}>
            <Route index element={<AdminOverview />} />
            <Route path="doctors" element={<AdminDoctors />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="patients" element={<AdminPatients />} />
            <Route path="appointments" element={<AdminAppointments />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="sms" element={<AdminSms />} />
            <Route path="doctors/:id/edit" element={<AdminDoctorEdit />} />
            <Route path="partners/:id/edit" element={<AdminPartnerEdit />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="security" element={<AdminSecurity />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}
